/** The list with the entry at `index` swapped with its neighbour before (-1) or after (1); unchanged at either end. */
export function moveAt<T>(items: readonly T[], index: number, step: -1 | 1): T[] {
	const target = index + step;
	if (target < 0 || target >= items.length) return [...items];
	const moved = [...items];
	[moved[index], moved[target]] = [moved[target], moved[index]];
	return moved;
}

/** A move in a list: a step to a neighbour (the arrow keys) or to a position (a drag). */
export type ListMove = { step: -1 | 1 } | { to: number };

/** The list with the entry at `index` taken out and put back at `to` (clamped to the list). */
export function moveTo<T>(items: readonly T[], index: number, to: number): T[] {
	const moved = [...items];
	const [item] = moved.splice(index, 1);
	moved.splice(Math.max(0, Math.min(to, moved.length)), 0, item);
	return moved;
}

/** The list with the first entry `matches` picks moved; unchanged when none matches or a step leaves the list. */
export function moveWhere<T>(
	items: readonly T[],
	matches: (item: T) => boolean,
	move: ListMove
): T[] {
	const index = items.findIndex(matches);
	if (index < 0) return [...items];
	return 'step' in move ? moveAt(items, index, move.step) : moveTo(items, index, move.to);
}
